import {
  MouseEvent,
  ChangeEvent,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  NoSymbolIcon,
  TruckIcon,
  CubeIcon,
  LockClosedIcon,
  CheckCircleIcon,
} from '@heroicons/react/20/solid';
import {
  useReadLocalStorage,
  useDocumentTitle,
  useDebounce,
} from 'usehooks-ts';
import { useAuthHttp, WarehouseClientOption, WarehouseOption } from '@hooks';
import { FilterService, UserRoleService } from '@services';
import {
  fetchLspWarehouses,
  fetchShipperWarehouses,
  fetchWarehouseClients,
} from '@api';
import { useAuth } from '@context';
import { useAtomValue } from 'jotai';
import {
  LIST_PRODUCTS_URL,
  LIST_SKUS_URL,
  GET_SKU_TOTAL_STOCK_METRICS,
  DEBOUNCE_TIME_IN_MS,
} from '@constants';
import { ToggleButtonGroup } from '@tailwindComponents/ToggleButtonGroup';
import {
  ProductGroupListing,
  productGroupKey,
} from '@views/SKUMaster/components/productGroups';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { stockRefreshAtom } from '../../store/jotai';
import { StockBySKUTablePaginationActions } from './components';
import { CollapsibleRow } from './components/CollapsibleRow';
import {
  ProductStockRow,
  productStockTotals,
} from './components/ProductStockRow';

import {
  InventorySKUListing,
  StockMetrics,
  TotalResults,
} from './components/types';
import { getLastItemSort, updateLastItemSorts } from '../../utils/pagination';
import {
  displayedBlockedStock,
  displayedLostStock,
  displayedLostProducts,
  displayedSurplusStock,
  displayedSurplusProducts,
} from '../../utils/stockDisplay';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Button } from '../../tailwindComponents/Button';

import { PageHeader } from '../../tailwindComponents/PageHeader';

export function StockBySKU() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authHttp = useAuthHttp();
  const { isWarehouseClient, isWarehouseManager } = UserRoleService();
  const darkMode = useReadLocalStorage('usehooks-ts-dark-mode');
  const { getFilterValue, setFilterValue } = FilterService();

  const [skuStockWarehouseId, setSkuStockWarehouseId] = useState(
    getFilterValue('skuStockWarehouseId')
  );

  const [skuStockShipperId, setSkuStockShipperId] = useState(
    getFilterValue('skuStockShipperId')
  );

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string | null>('');
  const [isOrderAscending, setIsOrderAscending] = useState(true);
  const [isLoadingSkus, setIsLoadingSkus] = useState(false);
  const [skus, setSkus] = useState<InventorySKUListing[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'products' | 'skus'>('products');
  const [productGroups, setProductGroups] = useState<ProductGroupListing[]>([]);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [productLastItemSorts, setProductLastItemSorts] = useState<object[]>(
    []
  );
  const [totalStockCounts, setTotalStockCounts] = useState<
    TotalResults | undefined
  >({
    totalStockQuantity: 0,
    blockedStockQuantity: 0,
    stockDiscrepancy: 0,
    inInboundTransitStockQuantity: 0,
    inOutboundTransitStockQuantity: 0,
    allocatedStockQuantity: 0,
    availableStockQuantity: 0,
    availableProductsQuantity: 0,
    allocatedProductsQuantity: 0,
    blockedProductsQuantity: 0,
    inInboundTransitProductsQuantity: 0,
    inOutboundTransitProductsQuantity: 0,
    totalProductsQuantity: 0,
    stockDiscrepancyProductsQuantity: 0,
  });
  const [stockMetrics, setStockMetrics] = useState<StockMetrics | undefined>();
  const [archivedSkuCount, setArchivedSkuCount] = useState<number | null>(null);

  // Stat-card figure: when the SKUs hold more than one product per stock unit, the PRODUCT total is
  // the headline (with a "products" label) and the stock-unit total is the muted secondary.
  const cardFigure = (stock?: number, products?: number) => {
    const s = stock ?? 0;
    const p = products ?? 0;
    const differ = p !== s;
    return (
      <>
        <p className='text-lg font-semibold text-gray-900 font-inter'>
          {(differ ? p : s).toLocaleString()}
          {differ && (
            <span className='ml-1 text-xs font-normal text-gray-600'>
              products
            </span>
          )}
        </p>
        {differ && (
          <p className='text-xs text-gray-600 font-inter'>
            {s.toLocaleString()} stock unit{s === 1 ? '' : 's'}
          </p>
        )}
      </>
    );
  };

  const availableLooseProducts =
    totalStockCounts?.availableLooseProductsQuantity ?? 0;
  const allocatedLooseProducts =
    totalStockCounts?.allocatedLooseProductsQuantity ?? 0;
  const outboundLooseProducts =
    totalStockCounts?.inOutboundTransitLooseProductsQuantity ?? 0;
  const totalLooseProducts =
    availableLooseProducts + allocatedLooseProducts + outboundLooseProducts;

  const metricsAvailableLoose = stockMetrics?.availableLooseProducts ?? 0;
  const metricsAllocatedLoose = stockMetrics?.allocatedLooseProducts ?? 0;
  const metricsOutboundLoose =
    stockMetrics?.inOutboundTransitLooseProducts ?? 0;
  const metricsTotalLoose =
    metricsAvailableLoose + metricsAllocatedLoose + metricsOutboundLoose;

  const withLoose = (products: number | undefined, loose: number) =>
    (products ?? 0) + loose;

  // Table TOTAL-row figure (compact, inline): the product total is the headline, the stock-unit
  // total is the muted secondary. A single number when they match.
  const totalFigure = (stock?: number, products?: number) => {
    const s = stock ?? 0;
    const p = products ?? 0;
    if (p === s) return <>{s.toLocaleString()}</>;
    return (
      <span className='inline-flex flex-col items-center leading-tight'>
        <span>
          {p.toLocaleString()}
          <span className='ml-1 text-xs font-normal text-gray-600'>
            products
          </span>
        </span>
        <span className='text-xs font-normal text-gray-600'>
          {s.toLocaleString()} stock unit{s === 1 ? '' : 's'}
        </span>
      </span>
    );
  };

  const pageDisplayedTotals = skus.reduce(
    (totals, row) => {
      const productsPerSku = row.amountOfProductInSku || 1;
      const blockedStock = displayedBlockedStock(
        row.stockByWarehouse,
        skuStockWarehouseId
      );
      return {
        blockedStock: totals.blockedStock + blockedStock,
        blockedProducts: totals.blockedProducts + blockedStock * productsPerSku,
        lostStock:
          totals.lostStock +
          displayedLostStock(row.stockByWarehouse, skuStockWarehouseId),
        lostProducts:
          totals.lostProducts +
          displayedLostProducts(row.stockByWarehouse, skuStockWarehouseId),
        surplusStock:
          totals.surplusStock +
          displayedSurplusStock(row.stockByWarehouse, skuStockWarehouseId),
        surplusProducts:
          totals.surplusProducts +
          displayedSurplusProducts(row.stockByWarehouse, skuStockWarehouseId),
      };
    },
    {
      blockedStock: 0,
      blockedProducts: 0,
      lostStock: 0,
      lostProducts: 0,
      surplusStock: 0,
      surplusProducts: 0,
    }
  );

  const [isInitialFetch, setIsInitialFetch] = useState(
    import.meta.env.MODE === 'development'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME_IN_MS);
  const stockRefresh = useAtomValue(stockRefreshAtom);

  const handleChangeSearchQuery = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const [lastItemSorts, setLastItemSorts] = useState<object[]>([]);

  useDocumentTitle('FLOX - Stock');

  const handleSortColumn = (column: string) => {
    if (viewMode === 'products') return;
    if (orderBy !== column) {
      setOrderBy(column);
      setIsOrderAscending(true);
    } else if (isOrderAscending) {
      setIsOrderAscending(false);
    } else {
      setOrderBy(null);
    }
  };

  const sharedListFilters = () => ({
    searchQuery: debouncedSearchQuery,
    minTotalStock: 1,
    isPalletisingRequired: false,
    warehouseIds:
      skuStockWarehouseId === 'All' || skuStockWarehouseId === null
        ? null
        : [skuStockWarehouseId],
    ...(isWarehouseManager(user) && {
      shipperBusinessId:
        skuStockShipperId === 'All' || skuStockShipperId === null
          ? null
          : skuStockShipperId,
    }),
  });

  const latestRequestId = useRef(0);
  const hasMountedRef = useRef(false);

  const fetchSkus = async (
    isGoingNext: boolean | undefined,
    requestId: number
  ) => {
    const { data } = await authHttp.post(LIST_SKUS_URL, {
      ...sharedListFilters(),
      pagination: {
        itemsPerPage,
        lastItemSort: getLastItemSort(isGoingNext, lastItemSorts),
      },
      orderBy,
      isOrderAscending,
    });

    if (requestId !== latestRequestId.current) return;

    setSkus(data.skus);
    setTotalCount(data.totalCount);
    setTotalStockCounts(data.totalResults);

    updateLastItemSorts(isGoingNext, setLastItemSorts, data.lastItemSort);
  };

  const fetchProductGroups = async (
    isGoingNext: boolean | undefined,
    requestId: number
  ) => {
    const { data } = await authHttp.post(LIST_PRODUCTS_URL, {
      ...sharedListFilters(),
      pagination: {
        itemsPerPage,
        lastItemSort: getLastItemSort(isGoingNext, productLastItemSorts),
      },
    });

    if (requestId !== latestRequestId.current) return;

    setProductGroups(data.products);
    setProductTotalCount(data.totalCount);

    updateLastItemSorts(
      isGoingNext,
      setProductLastItemSorts,
      data.lastItemSort
    );
  };

  const fetchRows = async (isGoingNext: boolean | undefined = undefined) => {
    if (isInitialFetch) {
      setIsInitialFetch(false);
      return;
    }
    if (isGoingNext !== undefined && isLoadingSkus) return;

    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setIsLoadingSkus(true);
    try {
      if (viewMode === 'products') {
        await fetchProductGroups(isGoingNext, requestId);
      } else {
        await fetchSkus(isGoingNext, requestId);
      }

      if (requestId === latestRequestId.current && isGoingNext === undefined) {
        setPage(0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (requestId === latestRequestId.current) {
        setIsLoadingSkus(false);
      }
    }
  };

  const fetchStockMetrics = async (signal?: AbortSignal) => {
    try {
      const { data } = await authHttp.post(
        GET_SKU_TOTAL_STOCK_METRICS,
        {
          minTotalStock: 1,
          isPalletisingRequired: false,
          warehouseIds:
            skuStockWarehouseId === 'All' || skuStockWarehouseId === null
              ? null
              : [skuStockWarehouseId],
          ...(isWarehouseManager(user) && {
            shipperBusinessId:
              skuStockShipperId === 'All' || skuStockShipperId === null
                ? null
                : skuStockShipperId,
          }),
        },
        { signal }
      );

      setStockMetrics(data.stockLevels);
      setArchivedSkuCount(data.archivedSkuCount ?? null);
    } catch (error) {
      if ((error as { code?: string })?.code === 'ERR_CANCELED') return;
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchStockMetrics(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuStockWarehouseId, skuStockShipperId, stockRefresh]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 1 && debouncedSearchQuery.length < 4)
      return;
    setLastItemSorts([]);
    setProductLastItemSorts([]);
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // Initial data load
  useEffect(() => {
    if (!hasMountedRef.current) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialFetch, skuStockShipperId]);

  useEffect(() => {
    if (!hasMountedRef.current) return;
    setLastItemSorts([]);
    setProductLastItemSorts([]);
    setPage(0);
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const handleChangePage = async (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (newPage > page) {
      await fetchRows(true);
    } else if (newPage < page) {
      await fetchRows(false);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
  };

  const [warehouseOptions, setWarehousesOptions] = useState<WarehouseOption[]>(
    []
  );

  const fetchWarehouses = async () => {
    try {
      const warehouses = isWarehouseClient(user)
        ? await fetchShipperWarehouses(authHttp)
        : await fetchLspWarehouses(authHttp);
      setWarehousesOptions(warehouses);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const [warehouseClientOptions, setWarehouseClientOptions] = useState<
    WarehouseClientOption[]
  >([]);

  const fetchClients = async () => {
    try {
      const clients = await fetchWarehouseClients(authHttp);
      setWarehouseClientOptions(clients);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchWarehouses();
      if (isWarehouseManager(user)) {
        await fetchClients();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuStockWarehouseId]);

  useEffect(() => {
    if (!hasMountedRef.current) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, orderBy, isOrderAscending]);

  useEffect(() => {
    if (stockRefresh === 0) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockRefresh]);

  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  const handleChangeWarehouse = (option: {
    label: string;
    value: string | number;
  }) => {
    setSkuStockWarehouseId(option.value as string);
    setFilterValue('skuStockWarehouseId', option.value as string);
  };

  const handleChangeClient = (option: {
    label: string;
    value: string | number;
  }) => {
    setSkuStockShipperId(option.value as string);
    setFilterValue('skuStockShipperId', option.value as string);
  };

  const orderByArrow = (column: string) => {
    if (viewMode === 'products') return null;
    if (orderBy === column) {
      return isOrderAscending ? (
        <ChevronUpIcon className='inline h-4 w-4' />
      ) : (
        <ChevronDownIcon className='inline h-4 w-4' />
      );
    }
    return <ChevronUpDownIcon className='inline h-4 w-4 text-gray-400' />;
  };

  const isProductsMode = viewMode === 'products';
  const hasTableRows =
    (viewMode === 'products' ? productGroups.length : skus.length) > 0;
  const groupedPageTotals = useMemo(
    () =>
      productStockTotals(
        productGroups.flatMap((group) => group.skus),
        skuStockWarehouseId
      ),
    [productGroups, skuStockWarehouseId]
  );
  const rowsTotalCount = isProductsMode ? productTotalCount : totalCount;
  const sortableColumnClasses = (hoverClasses: string) =>
    isProductsMode ? '' : ` cursor-pointer ${hoverClasses}`;

  const tableHeadHeight = '2.2813rem';
  const tableRowHeight = '3.3125rem';

  return (
    <>
      <PageHeader
        header='Stock'
        pages={[
          { name: 'Noticeboard', href: '/', current: false },
          { name: 'Stock', href: '/stock', current: true },
        ]}
      >
        <div className='flex gap-2'>
          {isWarehouseClient(user) && (
            <Button
              label='Add product'
              intent='primary'
              size='sm'
              startIcon={<PlusIcon className='h-4 w-4' />}
              onClick={() => navigate('/sku/create')}
            />
          )}
          <Button
            label='Export'
            intent='secondary'
            size='sm'
            startIcon={<ArrowDownTrayIcon className='h-4 w-4' />}
          />
        </div>
      </PageHeader>
      <main className='flex flex-col gap-4 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8 relative'>
        {/* Stat Cards Row */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${
            archivedSkuCount === null ? 'lg:grid-cols-6' : 'lg:grid-cols-7'
          }`}
        >
          {/* Total Stock */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <CubeIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>Total Stock</p>
              {cardFigure(
                stockMetrics?.totalStock,
                withLoose(stockMetrics?.totalProducts, metricsTotalLoose)
              )}
            </div>
          </div>
          {/* Blocked */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <NoSymbolIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>Blocked</p>
              <p className='text-lg font-semibold text-gray-900 font-inter'>
                {stockMetrics?.blockedStock?.toLocaleString() ?? 0}
              </p>
              <p className='text-xs text-gray-600 font-inter'>stock units</p>
            </div>
          </div>
          {/* In Despatch */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <TruckIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>In Despatch</p>
              {cardFigure(
                stockMetrics?.inOutboundTransitStock,
                withLoose(
                  stockMetrics?.inOutboundTransitProducts,
                  metricsOutboundLoose
                )
              )}
            </div>
          </div>
          {/* In Transit */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <TruckIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>In Transit</p>
              {cardFigure(
                stockMetrics?.inInboundTransitStock,
                stockMetrics?.inInboundTransitProducts
              )}
            </div>
          </div>
          {/* Allocated */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <LockClosedIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>Allocated</p>
              {cardFigure(
                stockMetrics?.allocatedStock,
                withLoose(
                  stockMetrics?.allocatedProducts,
                  metricsAllocatedLoose
                )
              )}
            </div>
          </div>
          {/* Available */}
          <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
            <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
              <CheckCircleIcon className='h-5 w-5 text-gray-600' />
            </div>
            <div>
              <p className='text-xs text-gray-500 font-inter'>Available</p>
              {cardFigure(
                stockMetrics?.availableStock,
                withLoose(
                  stockMetrics?.availableProducts,
                  metricsAvailableLoose
                )
              )}
            </div>
          </div>
          {/* Archived */}
          {archivedSkuCount !== null && (
            <div className='bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3'>
              <div className='flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'>
                <ArchiveBoxIcon className='h-5 w-5 text-gray-600' />
              </div>
              <div>
                <p className='text-xs text-gray-500 font-inter'>Archived</p>
                <p className='text-lg font-semibold text-gray-900 font-inter'>
                  {archivedSkuCount.toLocaleString()}
                </p>
                <p className='text-xs text-gray-600 font-inter'>
                  SKU{archivedSkuCount === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Filters Row */}
        <div className='flex flex-col lg:flex-row gap-3 items-start lg:items-center'>
          <div className='flex flex-wrap gap-3 items-center'>
            <ToggleButtonGroup
              label='View by'
              options={[
                { value: 'products', label: 'Product' },
                { value: 'skus', label: 'SKU' },
              ]}
              selected={viewMode}
              onChange={(value) => setViewMode(value as 'products' | 'skus')}
            />
            {/* Search */}
            <Input
              name='search'
              label=''
              placeholder='Search stock...'
              value={searchQuery}
              onChange={handleChangeSearchQuery}
              sizeVariant='sm'
            />
            {/* Warehouse filter */}
            <Select
              sizeVariant='sm'
              options={[
                { label: 'All warehouses', value: 'All' },
                ...warehouseOptions.map((option) => ({
                  label: option.warehouseName,
                  value: option.warehouseId,
                })),
              ]}
              selected={
                skuStockWarehouseId
                  ? {
                      label:
                        warehouseOptions.find(
                          (o) => o.warehouseId === skuStockWarehouseId
                        )?.warehouseName || 'All warehouses',
                      value: skuStockWarehouseId,
                    }
                  : { label: 'All warehouses', value: 'All' }
              }
              setSelected={handleChangeWarehouse}
              orientation='horizontal'
            />
            {isWarehouseManager(user) && (
              <Select
                sizeVariant='sm'
                options={[
                  { label: 'All shippers', value: 'All' },
                  ...warehouseClientOptions.map((option) => ({
                    label: option.userBusinessName,
                    value: option.userBusinessId,
                  })),
                ]}
                selected={
                  skuStockShipperId
                    ? {
                        label:
                          warehouseClientOptions.find(
                            (o) => o.userBusinessId === skuStockShipperId
                          )?.userBusinessName || 'All shippers',
                        value: skuStockShipperId,
                      }
                    : { label: 'All shippers', value: 'All' }
                }
                setSelected={handleChangeClient}
                orientation='horizontal'
              />
            )}
          </div>
        </div>
        {/* SKU table */}
        <div
          className={`rounded-xl overflow-x-auto relative bg-white ${
            darkMode
              ? 'border border-gray-300'
              : 'shadow-sm border border-gray-200'
          }`}
        >
          <table aria-label='collapsible table' className='w-full table-auto'>
            <thead className='bg-gray-50'>
              <tr className='border-b border-gray-200'>
                <th className='text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[12rem]'>
                  {isProductsMode ? (
                    <span>Product</span>
                  ) : (
                    <button
                      type='button'
                      onClick={() => handleSortColumn('Name')}
                      className='cursor-pointer hover:text-gray-700'
                    >
                      SKU Name & Details {orderByArrow('Name')}
                    </button>
                  )}
                </th>
                <th
                  onClick={() => handleSortColumn('SkuId')}
                  className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[8rem]${sortableColumnClasses(
                    'hover:text-gray-700'
                  )}`}
                >
                  {isProductsMode ? 'Product ID' : 'SKU ID'}{' '}
                  {orderByArrow('SkuId')}
                </th>
                <th
                  onClick={() => handleSortColumn('InInboundTransitStockCount')}
                  className={`text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[6rem]${sortableColumnClasses(
                    'hover:text-gray-700'
                  )}`}
                >
                  In Transit {orderByArrow('InInboundTransitStockCount')}
                </th>
                <th
                  onClick={() => handleSortColumn('AvailableStockCount')}
                  className={`text-center text-xs font-medium text-green-600 uppercase tracking-wider px-4 py-3 min-w-[6rem] bg-green-50${sortableColumnClasses(
                    'hover:text-green-700'
                  )}`}
                >
                  Available {orderByArrow('AvailableStockCount')}
                </th>
                {isWarehouseClient(user) && (
                  <th className='text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[7rem]'>
                    Actions
                  </th>
                )}
                <th
                  onClick={() => handleSortColumn('AllocatedStockCount')}
                  className={`text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[6rem]${sortableColumnClasses(
                    'hover:text-gray-700'
                  )}`}
                >
                  Allocated {orderByArrow('AllocatedStockCount')}
                </th>
                <th
                  onClick={() => handleSortColumn('OutForDeliveryStockCount')}
                  className={`text-center text-xs font-medium text-blue-600 uppercase tracking-wider px-4 py-3 min-w-[7rem] bg-blue-50${sortableColumnClasses(
                    'hover:text-blue-700'
                  )}`}
                >
                  Out for delivery {orderByArrow('OutForDeliveryStockCount')}
                </th>
                <th
                  onClick={() => handleSortColumn('BlockedStockCount')}
                  className={`text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[6rem]${sortableColumnClasses(
                    'hover:text-gray-700'
                  )}`}
                >
                  Blocked {orderByArrow('BlockedStockCount')}
                </th>
                <th
                  onClick={() => handleSortColumn('TotalStockCount')}
                  className={`text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 min-w-[7rem] bg-blue-50${sortableColumnClasses(
                    'hover:text-gray-700'
                  )}`}
                >
                  Total {orderByArrow('TotalStockCount')}
                </th>
                <th
                  onClick={() => handleSortColumn('StockDiscrepancyCount')}
                  className={`text-center text-xs font-medium text-amber-600 uppercase tracking-wider px-4 py-3 min-w-[7rem] bg-amber-50${sortableColumnClasses(
                    'hover:text-amber-700'
                  )}`}
                >
                  Discrepancy {orderByArrow('StockDiscrepancyCount')}
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {isProductsMode
                ? productGroups.map((group) => (
                    <ProductStockRow
                      key={productGroupKey(group)}
                      group={group}
                      warehouseId={skuStockWarehouseId}
                      showOwner={isWarehouseManager(user)}
                    />
                  ))
                : skus.map((row) => (
                    <CollapsibleRow
                      key={row.id}
                      row={row}
                      warehouseId={skuStockWarehouseId}
                    />
                  ))}
              {!isLoadingSkus && !hasTableRows && (
                <tr>
                  <td
                    colSpan={isWarehouseClient(user) ? 10 : 9}
                    className='px-4 py-16 text-center'
                  >
                    <p className='text-sm font-medium text-gray-700'>
                      No stock to show
                    </p>
                    <p className='mt-1 text-sm text-gray-500'>
                      {debouncedSearchQuery
                        ? 'Nothing matches your search or filters.'
                        : 'Stock appears here once goods are received into a warehouse.'}
                    </p>
                  </td>
                </tr>
              )}
              {/* Totals row */}
              {hasTableRows && isProductsMode && (
                <tr className='bg-gray-50 font-semibold'>
                  <td className='px-4 py-3' />
                  <td className='px-4 py-3 text-sm text-gray-900'>TOTAL</td>
                  <td className='text-center px-4 py-3 text-sm text-gray-900'>
                    {groupedPageTotals.inTransitProducts.toLocaleString()}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-green-600 bg-green-50 font-semibold'>
                    {groupedPageTotals.availableProducts.toLocaleString()}
                  </td>
                  {isWarehouseClient(user) && <td className='px-4 py-3' />}
                  <td className='text-center px-4 py-3 text-sm text-gray-900'>
                    {groupedPageTotals.allocatedProducts.toLocaleString()}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-blue-600 bg-blue-50 font-semibold'>
                    {groupedPageTotals.outForDeliveryProducts.toLocaleString()}
                  </td>
                  <td className='text-center px-4 py-3 text-sm'>
                    <span
                      className={
                        groupedPageTotals.blockedProducts > 0
                          ? 'text-red-600 font-semibold'
                          : 'text-gray-900'
                      }
                    >
                      {groupedPageTotals.blockedProducts.toLocaleString()}
                    </span>
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-gray-900 bg-blue-50 font-semibold'>
                    {groupedPageTotals.totalProducts.toLocaleString()}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-amber-600 bg-amber-50 font-semibold'>
                    <span className='inline-flex flex-col items-center leading-tight'>
                      {groupedPageTotals.lostProducts.toLocaleString()}
                      {groupedPageTotals.surplusProducts > 0 && (
                        <span className='text-xs font-normal text-gray-600'>
                          · {numberToLocale(groupedPageTotals.surplusProducts)}{' '}
                          surplus
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              )}
              {hasTableRows && !isProductsMode && (
                <tr className='bg-gray-50 font-semibold'>
                  <td className='px-4 py-3' />
                  <td className='px-4 py-3 text-sm text-gray-900'>TOTAL</td>
                  <td className='text-center px-4 py-3 text-sm text-gray-900'>
                    {totalFigure(
                      totalStockCounts?.inInboundTransitStockQuantity,
                      totalStockCounts?.inInboundTransitProductsQuantity
                    )}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-green-600 bg-green-50 font-semibold'>
                    {totalFigure(
                      totalStockCounts?.availableStockQuantity,
                      withLoose(
                        totalStockCounts?.availableProductsQuantity,
                        availableLooseProducts
                      )
                    )}
                  </td>
                  {isWarehouseClient(user) && <td className='px-4 py-3' />}
                  <td className='text-center px-4 py-3 text-sm text-gray-900'>
                    {totalFigure(
                      totalStockCounts?.allocatedStockQuantity,
                      withLoose(
                        totalStockCounts?.allocatedProductsQuantity,
                        allocatedLooseProducts
                      )
                    )}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-blue-600 bg-blue-50 font-semibold'>
                    {totalFigure(
                      totalStockCounts?.inOutboundTransitStockQuantity,
                      withLoose(
                        totalStockCounts?.inOutboundTransitProductsQuantity,
                        outboundLooseProducts
                      )
                    )}
                  </td>
                  <td className='text-center px-4 py-3 text-sm'>
                    <span
                      className={`${
                        pageDisplayedTotals.blockedStock > 0
                          ? 'text-red-600 font-semibold'
                          : 'text-gray-900'
                      }`}
                    >
                      {totalFigure(
                        pageDisplayedTotals.blockedStock,
                        pageDisplayedTotals.blockedProducts
                      )}
                    </span>
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-gray-900 bg-blue-50 font-semibold'>
                    {totalFigure(
                      totalStockCounts?.totalStockQuantity,
                      withLoose(
                        totalStockCounts?.totalProductsQuantity,
                        totalLooseProducts
                      )
                    )}
                  </td>
                  <td className='text-center px-4 py-3 text-sm text-amber-600 bg-amber-50 font-semibold'>
                    <span className='inline-flex flex-col items-center leading-tight'>
                      {totalFigure(
                        pageDisplayedTotals.lostStock,
                        pageDisplayedTotals.lostProducts
                      )}
                      {(pageDisplayedTotals.surplusProducts > 0 ||
                        pageDisplayedTotals.surplusStock > 0) && (
                        <span className='text-xs font-normal text-gray-600'>
                          ·{' '}
                          {numberToLocale(
                            pageDisplayedTotals.surplusProducts ||
                              pageDisplayedTotals.surplusStock
                          )}{' '}
                          surplus
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className='bg-white border-t border-gray-200'>
                <td
                  className='px-4 py-3'
                  colSpan={isWarehouseClient(user) ? 10 : 9}
                >
                  <div className='flex items-center justify-between'>
                    <p className='text-sm text-gray-500'>
                      Showing{' '}
                      <span className='font-medium'>
                        {page * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className='font-medium'>
                        {Math.min((page + 1) * itemsPerPage, rowsTotalCount)}
                      </span>{' '}
                      of <span className='font-medium'>{rowsTotalCount}</span>{' '}
                      {isProductsMode ? 'products' : 'SKUs'}
                    </p>
                    <div className='flex items-center gap-4'>
                      <StockBySKUTablePaginationActions
                        count={rowsTotalCount}
                        page={page}
                        rowsPerPage={itemsPerPage}
                        onPageChange={handleChangePage}
                      />
                      <div className='flex items-center gap-2'>
                        <span className='text-sm text-gray-500'>
                          Show per page:
                        </span>
                        <select
                          aria-label='Rows per page'
                          value={itemsPerPage}
                          onChange={handleChangeRowsPerPage}
                          className='rounded-md border border-gray-300 bg-white py-1.5 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500'
                        >
                          {[5, 8, 10, 25, 50, 100].map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          {isLoadingSkus && (
            <div
              className='absolute top-[2.2813rem] left-0 w-full flex justify-center items-center bg-white/50'
              style={{
                height: `calc(100% - ${tableHeadHeight} - ${tableRowHeight})`,
              }}
            >
              <FloxSpinner size='md' />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
